import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"

import { useTailwindBreakpoints } from "components/hooks/use-tailwind-breakpoints"

import type { CornerProps } from "../clipped-box/clipped-box"
import { ClippedBox } from "../clipped-box/clipped-box"
import { ArrowDown as ArrowDownIcon } from "../icons/arrow-down"
import { CheckMark } from "../icons/checkmark"
import { LeftArrow as LeftArrowIcon } from "../icons/left-arrow"
import { Button } from "../landing/button"
import { Text } from "../text"
import styles from "./select.module.scss"

export type Option = {
  label: string
  value: string
}

type SelectProps = {
  theme?: "light" | "dark" | "bordered"
  options: Option[]
  value?: string
  alignTo?: "left" | "right"
  className?: string
  selectClassName?: string
  label?: string
  labelClassName?: string
  onChange?: (value: string) => void
  onClick?: () => void
  placeholder?: string
  disabled?: boolean
  corners?: CornerProps
  clipK?: string
  queryParam?: string
  centered?: boolean
}

export function Select({
  options,
  label,
  labelClassName,
  value,
  alignTo = "left",
  theme = "dark",
  className = "",
  selectClassName,
  onChange,
  onClick,
  disabled,
  corners,
  clipK,
  placeholder = "Select",
  queryParam,
  centered = false,
}: SelectProps) {
  const router = useRouter()
  const { query } = router

  const [isOpened, setIsOpened] = useState(false)
  const toggleIsOpened = useCallback(() => (onClick ? onClick() : setIsOpened(!isOpened)), [isOpened, onClick])
  const selectedLabel = options.find(option => option.value === value)?.label || placeholder
  const { isMobile } = useTailwindBreakpoints()

  useEffect(() => {
    const newQueryParam = queryParam && query[queryParam] && query[queryParam] !== value
    const dataItem = newQueryParam && options.find(d => d.value === query[queryParam])
    if (dataItem) {
      onChange?.(dataItem.value)
    }
  }, [])

  return (
    <div className={`inline-block relative ${disabled ? "" : "cursor-pointer"}`}>
      <label className={["inline-flex flex-col", className].join(" ")}>
        {label && <span className={["text-white mb-[0.25rem]", labelClassName].join(" ")}>{label}</span>}
        <Button noPadding noVolume type="transparent" onClick={toggleIsOpened} className="truncate" disabled={disabled}>
          <ClippedBox
            row
            className={[
              "px-4 py-3 gap-3 items-center rounded-lg",
              styles[`select__${theme}`],
              selectClassName,
              centered ? "justify-center" : "",
            ].join(" ")}
            clipK={clipK}
            corners={corners}
          >
            <Text className={`uppercase lg:normal-case ${theme === "light" ? "text-shadow-without-blur" : ""}`}>
              {selectedLabel}
            </Text>
            {theme === "dark" && (
              <LeftArrowIcon
                className={`transition-transform ${isOpened ? "rotate-90" : "-rotate-90"}`}
                pale
                h="1rem"
                w="0.625rem"
              />
            )}

            {theme === "light" && (
              <ArrowDownIcon
                className="relative -top-[0.125rem]"
                h={isMobile ? "1.4rem" : "1.625rem"}
                w={isMobile ? "1.4rem" : "1.625rem"}
              />
            )}
          </ClippedBox>
        </Button>
      </label>
      {isOpened && (
        <div
          className={[
            "absolute rounded-lg overflow-hidden z-20 max-h-[9rem] lg:max-h-[15rem] overflow-y-auto",
            `${styles["select_options-wrapper"]}`,
            label ? "top-20" : "top-16",
            alignTo === "left" ? "left-0" : "right-0",
            selectClassName,
          ].join(" ")}
        >
          {options.map(option => (
            <div
              key={option.value}
              className={`px-4 py-3 truncate ${styles[`select_option__${theme}`]} flex gap-4 w-full items-center ${
                centered ? "justify-center" : "justify-between"
              }`}
              onClick={() => {
                setIsOpened(false)
                onChange?.(option.value)
                if (queryParam) {
                  router.replace({ pathname: location.pathname, query: { ...query, [queryParam]: option.value } })
                }
              }}
            >
              <Text className="uppercase lg:normal-case">{option.label}</Text>
              {option.value === value && <CheckMark />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
